import React, { useState, useEffect } from 'react';
import { itemsService, addonsService } from '../services/api';
import { toast } from 'react-toastify';
import { PERMISSIONS, hasPermission } from '../utils/rbac';
import { useIsMobile } from '../hooks';
import { Hourglass as HourglassIcon, Lock as LockIcon, Package as PackageIcon, Plus as PlusIcon } from 'lucide-react';
import { usePmsDataRefresh } from '../agent/dataSync';

function ItemsManagement({ user }) {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [addOnServices, setAddOnServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('ALL');
  const [stockFilter, setStockFilter] = useState('ALL');
  const [showStockAdjustModal, setShowStockAdjustModal] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    addOnServiceId: '',
    unitPrice: '',
    quantity: '',
    minStockLevel: '',
    maxStockLevel: '',
    isActive: true
  });

  // Stock adjustment state
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: '',
    type: 'IN',
    reason: ''
  });

  // Check access - User needs inventory read permission
  const hasAccess = hasPermission(user?.role, PERMISSIONS.INVENTORY_READ);

  useEffect(() => {
    if (hasAccess) {
      loadItems();
      loadAddOnServices();
    }
  }, [hasAccess]);

  // Stock changed by the PMS agent: reload the list in place.
  usePmsDataRefresh(['inventory'], () => {
    if (hasAccess) loadItems({ silent: true });
  });

  // Debug: Log when addOnServices changes
  useEffect(() => {
    console.log('addOnServices state updated:', addOnServices);
    console.log('Is array?', Array.isArray(addOnServices));
    console.log('Length:', addOnServices?.length);
  }, [addOnServices]);

  // options.silent: reload without the loading state (used after agent changes)
  const loadItems = async (options) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await itemsService.getAll();
      // Handle different response structures
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          setItems(response.data);
        } else if (response.data.items && Array.isArray(response.data.items)) {
          setItems(response.data.items);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          setItems(response.data.data);
        } else {
          setItems([]);
        }
      } else if (Array.isArray(response)) {
        setItems(response);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error loading items:', error);
      if (!silent) {
        toast.error('Failed to load items');
        setItems([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadAddOnServices = async () => {
    try {
      console.log('Loading addon services...');
      const response = await addonsService.getAll();
      console.log('Raw API Response:', response);
      console.log('Response.data:', response?.data);
      
      // Handle different response structures based on API_CHANGES.md documentation
      // Expected structure: { success: true, data: { addOns: [...], total, page, limit } }
      if (response && response.data) {
        // Check for the correct API structure first: response.data.addOns
        if (response.data.addOns && Array.isArray(response.data.addOns)) {
          console.log('Found array in response.data.addOns (correct API format), length:', response.data.addOns.length);
          setAddOnServices(response.data.addOns);
        } else if (Array.isArray(response.data)) {
          console.log('Found array in response.data, length:', response.data.length);
          setAddOnServices(response.data);
        } else if (response.data.addOnServices && Array.isArray(response.data.addOnServices)) {
          console.log('Found array in response.data.addOnServices, length:', response.data.addOnServices.length);
          setAddOnServices(response.data.addOnServices);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          console.log('Found array in response.data.data, length:', response.data.data.length);
          setAddOnServices(response.data.data);
        } else {
          console.warn('No array found in response, setting empty array');
          console.log('Response structure:', JSON.stringify(response, null, 2));
          setAddOnServices([]);
        }
      } else if (Array.isArray(response)) {
        console.log('Response is array, length:', response.length);
        setAddOnServices(response);
      } else {
        console.warn('Response is not an array or object with data, setting empty array');
        setAddOnServices([]);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
      setAddOnServices([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAdjustmentChange = (e) => {
    const { name, value } = e.target;
    setAdjustmentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      sku: '',
      addOnServiceId: '',
      unitPrice: '',
      quantity: '',
      minStockLevel: '',
      maxStockLevel: '',
      isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      sku: item.sku || '',
      addOnServiceId: item.addOnServiceId || '',
      unitPrice: item.unitPrice || '',
      quantity: item.quantity || '',
      minStockLevel: item.minStockLevel || '',
      maxStockLevel: item.maxStockLevel || '',
      isActive: item.isActive !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      sku: '',
      addOnServiceId: '',
      unitPrice: '',
      quantity: '',
      minStockLevel: '',
      maxStockLevel: '',
      isActive: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.sku || !formData.addOnServiceId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const itemData = {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice) || 0,
        quantity: parseInt(formData.quantity) || 0,
        minStockLevel: parseInt(formData.minStockLevel) || 0,
        maxStockLevel: parseInt(formData.maxStockLevel) || 0
      };

      if (editingItem) {
        await itemsService.update(editingItem.id, itemData);
        toast.success('Item updated successfully');
      } else {
        await itemsService.create(itemData);
        toast.success('Item created successfully');
      }

      closeModal();
      loadItems();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.error || 'Failed to save item');
    }
  };

  const handleDelete = async (itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to delete "${itemName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await itemsService.delete(itemId, true); // permanent delete
      toast.success('Item deleted successfully');
      loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.error || 'Failed to delete item');
    }
  };

  const openStockAdjustModal = (item) => {
    setAdjustingItem(item);
    setAdjustmentData({
      quantity: '',
      type: 'IN',
      reason: ''
    });
    setShowStockAdjustModal(true);
  };

  const closeStockAdjustModal = () => {
    setShowStockAdjustModal(false);
    setAdjustingItem(null);
    setAdjustmentData({
      quantity: '',
      type: 'IN',
      reason: ''
    });
  };

  const handleStockAdjustment = async (e) => {
    e.preventDefault();

    if (!adjustmentData.quantity || !adjustmentData.reason) {
      toast.error('Please enter quantity and reason');
      return;
    }

    try {
      const adjustment = {
        quantity: parseInt(adjustmentData.quantity),
        type: adjustmentData.type,
        reason: adjustmentData.reason
      };

      await itemsService.adjustInventory(adjustingItem.id, adjustment);
      toast.success('Stock adjusted successfully');
      closeStockAdjustModal();
      loadItems();
    } catch (error) {
      console.error('Error adjusting stock:', error);
      toast.error(error.error || 'Failed to adjust stock');
    }
  };

  const getStockStatus = (item) => {
    if (item.quantity === 0) return 'OUT_OF_STOCK';
    if (item.quantity <= item.minStockLevel) return 'LOW_STOCK';
    return 'IN_STOCK';
  };

  const getStockBadge = (item) => {
    const status = getStockStatus(item);
    const colors = {
      'IN_STOCK': { bg: '#E1E9E2', color: '#0F2619', text: 'In Stock' },
      'LOW_STOCK': { bg: '#F3E6CB', color: '#684A1F', text: 'Low Stock' },
      'OUT_OF_STOCK': { bg: '#F2D9D3', color: '#5F241B', text: 'Out of Stock' }
    };
    const style = colors[status];
    
    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {style.text}
      </span>
    );
  };

  // Filter items
  const filteredItems = Array.isArray(items) ? items.filter(item => {
    const matchesSearch = 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesService = serviceFilter === 'ALL' || item.addOnServiceId === serviceFilter;
    
    const matchesStock = stockFilter === 'ALL' || 
      (stockFilter === 'LOW' && getStockStatus(item) === 'LOW_STOCK') ||
      (stockFilter === 'OUT' && getStockStatus(item) === 'OUT_OF_STOCK') ||
      (stockFilter === 'IN' && getStockStatus(item) === 'IN_STOCK');
    
    return matchesSearch && matchesService && matchesStock;
  }) : [];

  if (!hasAccess) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}><LockIcon size="1em" aria-hidden="true" /></div>
        <h3 style={{ margin: '0 0 10px 0', color: '#36433B' }}>Access Denied</h3>
        <p style={{ color: '#5F6B61' }}>You don't have permission to access items management.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '12px', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: '0', fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: '#1A3E2A' }}>
              Items & Inventory Management
            </h2>
            <p style={{ margin: '5px 0 0 0', color: '#5F6B61', fontSize: '16px' }}>
              Manage service items and track inventory ({filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'})
            </p>
          </div>
          <button
            onClick={openCreateModal}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#163626'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#1A3E2A'}
          >
            <span style={{ fontSize: '18px' }}><PlusIcon size="1em" aria-hidden="true" /></span>
            Add New Item
          </button>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'grid', 
          gap: '16px', 
          alignItems: 'center',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(220px, 1fr) 180px 150px',
          padding: '20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '1px solid #E3DFD3'
        }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Search Items
            </label>
            <input
              type="text"
              placeholder="Search by name, SKU, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
          </div>
          
          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Filter by Service
            </label>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="ALL">All Services</option>
              {Array.isArray(addOnServices) && addOnServices.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Stock Status
            </label>
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="ALL">All Status</option>
              <option value="IN">In Stock</option>
              <option value="LOW">Low Stock</option>
              <option value="OUT">Out of Stock</option>
            </select>
          </div>
        </div>
      </div>

      {/* Items Grid */}
      {loading ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px',
          fontSize: '18px',
          color: '#5F6B61'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
            Loading items...
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '2px dashed #D2CDBF'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}><PackageIcon size="1em" aria-hidden="true" /></div>
          <h3 style={{ margin: '0 0 10px 0', color: '#36433B', fontSize: '20px' }}>
            {items.length === 0 ? 'No items found' : 'No items match your filters'}
          </h3>
          <p style={{ color: '#5F6B61', margin: '0', fontSize: '16px' }}>
            {items.length === 0 
              ? 'Click "Add New Item" to create your first inventory item.' 
              : 'Try adjusting your search or filter criteria.'}
          </p>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: '20px' 
        }}>
          {filteredItems.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              addOnServices={addOnServices}
              onEdit={openEditModal}
              onDelete={handleDelete}
              onAdjustStock={openStockAdjustModal}
              getStockBadge={getStockBadge}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            borderRadius: '16px',
            padding: isMobile ? '16px' : '32px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1A3E2A' }}>
                {editingItem ? 'Edit Item' : 'Create New Item'}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#5F6B61',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: '20px' }}>
                {/* Item Name */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Item Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Cotton Robe - Large"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* SKU */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    SKU (Stock Keeping Unit) *
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., ROBE-COT-L"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Service */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Add-On Service *
                  </label>
                  <select
                    name="addOnServiceId"
                    value={formData.addOnServiceId}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">
                      {addOnServices.length === 0 ? 'No services available - Create addon services first' : 'Select a service'}
                    </option>
                    {Array.isArray(addOnServices) && addOnServices.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                  {addOnServices.length === 0 && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#A13F2F', 
                      marginTop: '4px',
                      marginBottom: 0
                    }}>
                      No addon services found. Please create addon services in the "Add-ons Management" tab first.
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Optional description..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Unit Price */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Unit Price (₹)
                  </label>
                  <input
                    type="number"
                    name="unitPrice"
                    value={formData.unitPrice}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Stock Levels */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                      Current Stock
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                      Min Level
                    </label>
                    <input
                      type="number"
                      name="minStockLevel"
                      value={formData.minStockLevel}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                      Max Level
                    </label>
                    <input
                      type="number"
                      name="maxStockLevel"
                      value={formData.maxStockLevel}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* Active Status */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                      Item is active and available for selection
                    </span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexDirection: isMobile ? 'column' : 'row' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    backgroundColor: '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingItem ? 'Update Item' : 'Create Item'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    flex: 1,
                    backgroundColor: '#5F6B61',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showStockAdjustModal && adjustingItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            borderRadius: '16px',
            padding: isMobile ? '16px' : '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1A3E2A' }}>
                Adjust Stock
              </h3>
              <button
                onClick={closeStockAdjustModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#5F6B61',
                  padding: '0'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ 
              backgroundColor: '#F5F3EB', 
              padding: '16px', 
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                {adjustingItem.name}
              </div>
              <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '8px' }}>
                SKU: {adjustingItem.sku}
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#1A3E2A' }}>
                Current Stock: {adjustingItem.quantity}
              </div>
            </div>

            <form onSubmit={handleStockAdjustment}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Adjustment Type *
                  </label>
                  <select
                    name="type"
                    value={adjustmentData.type}
                    onChange={handleAdjustmentChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="IN">Stock In (Add)</option>
                    <option value="OUT">Stock Out (Remove)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Quantity *
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={adjustmentData.quantity}
                    onChange={handleAdjustmentChange}
                    required
                    min="1"
                    placeholder="Enter quantity"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                    Reason *
                  </label>
                  <textarea
                    name="reason"
                    value={adjustmentData.reason}
                    onChange={handleAdjustmentChange}
                    required
                    placeholder="e.g., New stock received, Damaged items removed, etc."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {adjustmentData.quantity && (
                  <div style={{
                    backgroundColor: '#F0F3EE',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #1A3E2A'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#122E20' }}>
                      New Stock Level: {
                        adjustmentData.type === 'IN' 
                          ? adjustingItem.quantity + parseInt(adjustmentData.quantity || 0)
                          : adjustingItem.quantity - parseInt(adjustmentData.quantity || 0)
                      }
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexDirection: isMobile ? 'column' : 'row' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    backgroundColor: '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Adjust Stock
                </button>
                <button
                  type="button"
                  onClick={closeStockAdjustModal}
                  style={{
                    flex: 1,
                    backgroundColor: '#5F6B61',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Item Card Component
function ItemCard({ item, addOnServices, onEdit, onDelete, onAdjustStock, getStockBadge, isMobile }) {
  const service = Array.isArray(addOnServices) 
    ? addOnServices.find(s => s.id === item.addOnServiceId) 
    : null;
  
  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #E3DFD3',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      opacity: item.isActive ? 1 : 0.6
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: '8px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
            {item.name}
          </h4>
          <p style={{ margin: '0', fontSize: '12px', color: '#5F6B61' }}>
            SKU: {item.sku}
          </p>
        </div>
        {getStockBadge(item)}
      </div>

      {/* Service */}
      <div style={{ 
        backgroundColor: '#F5F3EB', 
        padding: '12px', 
        borderRadius: '8px', 
        marginBottom: '16px'
      }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
          Service
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
          {service?.name || 'Unknown Service'}
        </div>
      </div>

      {/* Description */}
      {item.description && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Description
          </div>
          <div style={{ fontSize: '14px', color: '#36433B' }}>
            {item.description}
          </div>
        </div>
      )}

      {/* Stock Details */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
        gap: '12px',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#F5F3EB',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Stock Quantity
          </div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
            {item.quantity}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Unit Price
          </div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
            ₹{item.unitPrice || 0}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Min Level
          </div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
            {item.minStockLevel}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
            Max Level
          </div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
            {item.maxStockLevel}
          </div>
        </div>
      </div>

      {/* Status */}
      <div style={{ marginBottom: '16px' }}>
        <span style={{
          backgroundColor: item.isActive ? '#E1E9E2' : '#F2D9D3',
          color: item.isActive ? '#0F2619' : '#5F241B',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          {item.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
        <button
          onClick={() => onAdjustStock(item)}
          style={{
            flex: '1',
            backgroundColor: '#A9613C',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Adjust Stock
        </button>
        <button
          onClick={() => onEdit(item)}
          style={{
            flex: '1',
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(item.id, item.name)}
          style={{
            backgroundColor: '#A13F2F',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export default ItemsManagement;
